<template lang="pug">
.faq-chat-page
  .title FAQs

  .faq-chat-toolbar
    .input-container.arrow
      select.select(v-model="selectedBrand" autocomplete="ño" @change="loadChat")
        option(v-for="brand in brands" :key="brand" :value="brand") {{ brand }}
      label.label(:class="{ show: selectedBrand }") Marca

  .faq-chat-loading(v-if="loading") Cargando chat...

  .faq-chat-layout(v-else-if="selectedBrand")
    .faq-text-panel
      .panel-title Faqs actuales
      .faq-text-content {{ chat.text || '(vacío)' }}

    .faq-chat-panel
      .panel-title Chat
      .faq-messages(ref="messagesContainer")
        .faq-message(v-for="(msg, index) in chat.messages" :key="index" :class="msg.role")
          .faq-message-meta
            span.faq-message-role {{ msg.role === 'user' ? 'Operador' : 'IA' }}
            span.faq-message-author(v-if="msg.author && msg.role === 'user'")
              shNameUser(:dni="msg.author")
            span.faq-message-time {{ formatDate(msg.createdAt) }}
          .faq-message-content {{ msg.content }}

        .faq-message.assistant.loading(v-if="sending")
          .faq-message-meta
            span.faq-message-role IA
          .faq-message-content Pensando...

      form.faq-chat-form(@submit.prevent="")
        textarea.faq-chat-input(
          v-model="draft"
          rows="3"
          placeholder="Ej: Cambia el plazo de cambios a 15 días hábiles..."
          :disabled="sending || loading"
        )
        shActionButton(
          :isForm="true"
          text="Enviar"
          textRunning="Enviando"
          :action="sendMessage"
          :disabled="!canSend"
        )

  .faq-chat-empty(v-else)
    | Selecciona una marca para editar las FAQs.
</template>

<script>
import gql from '@my/graphql'
import gqlFaq from '@/graphql/faq'
import shActionButton from '@/components/elements/ActionButton'
import shNameUser from '@/components/elements/NameUser'
import modals from '@my/store/modals'

export default {
  name: 'Faqs',

  components: { shActionButton, shNameUser },

  data () {
    return {
      selectedBrand: '',
      draft: '',
      loading: false,
      sending: false,
      chat: {
        brand: '',
        text: '',
        messages: []
      }
    }
  },

  computed: {
    brands () {
      return this.$store.state.variables.brands || []
    },
    canSend () {
      return Boolean(this.draft.trim()) && !this.sending && !this.loading && Boolean(this.selectedBrand)
    }
  },

  mounted () {
    if (this.brands.length > 0) {
      this.selectedBrand = this.brands[0]
      this.loadChat()
    }
  },

  watch: {
    brands (value) {
      if (value.length > 0 && !this.selectedBrand) {
        this.selectedBrand = value[0]
        this.loadChat()
      }
    },
    sending () {
      this.scrollToBottom()
    },
    'chat.messages.length' () {
      this.scrollToBottom()
    }
  },

  methods: {
    formatDate (value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    async loadChat () {
      if (!this.selectedBrand) return

      this.loading = true
      try {
        const { data } = await gql.query(gqlFaq.GET_CHAT, { brand: this.selectedBrand })
        this.chat = data.getFaqChat
      } catch (error) {
        console.log(error)
        modals.showMessage({ message: 'No se pudo cargar el chat de FAQs', type: 'error' })
      } finally {
        this.loading = false
        this.scrollToBottom()
      }
    },

    async sendMessage () {
      const message = this.draft.trim()
      if (!message || this.sending || !this.selectedBrand) return

      this.sending = true
      this.draft = ''

      this.chat.messages.push({
        role: 'user',
        content: message,
        author: this.$store.state.user.dni,
        createdAt: new Date().toISOString()
      })

      try {
        const { data } = await gql.mutate(gqlFaq.SEND_CHAT, {
          brand: this.selectedBrand,
          message
        })
        this.chat = data.sendFaqChat
      } catch (error) {
        console.log(error)
        modals.showMessage({ message: this.getErrorMessage(error), type: 'error' })
        await this.loadChat()
      } finally {
        this.sending = false
        this.scrollToBottom()
      }
    },

    scrollToBottom () {
      const self = this
      this.$nextTick(function () {
        const container = self.$refs.messagesContainer
        if (!container) return
        container.scrollTop = container.scrollHeight
        requestAnimationFrame(function () {
          container.scrollTop = container.scrollHeight
        })
      })
    },

    getErrorMessage (error) {
      if (!error) return 'Error al enviar mensaje'
      if (error.graphQLErrors && error.graphQLErrors[0] && error.graphQLErrors[0].message) {
        return error.graphQLErrors[0].message
      }
      if (error.message) return error.message
      return 'Error al enviar mensaje'
    }
  }
}
</script>

<style lang="stylus" scoped>
.faq-chat-page
  padding 0 0.5rem
  width 100%
  max-width 100%
  box-sizing border-box
  display flex
  flex-direction column
  height calc(100dvh - 4.5rem)
  max-height calc(100dvh - 4.5rem)
  overflow hidden

.title
  flex-shrink 0

.faq-chat-toolbar
  margin 0.4rem 0 0.75rem
  max-width 320px
  flex-shrink 0

.faq-chat-layout
  display grid
  grid-template-columns 1fr 1fr
  gap 1.5rem
  align-items stretch
  flex 1
  min-height 0
  overflow hidden

  @media (max-width: 900px)
    grid-template-columns 1fr

.faq-text-panel,
.faq-chat-panel
  display flex
  flex-direction column
  gap 0.75rem
  padding 1.25rem
  border-radius 1rem
  border 1px solid rgba(0, 0, 0, 0.1)
  background rgba(255, 255, 255, 0.6)
  min-height 0
  overflow hidden

.panel-title
  font-weight 600
  font-size 1.2rem
  opacity 0.85
  flex-shrink 0

.faq-text-content
  white-space pre-wrap
  line-height 1.65
  font-size 1.2rem
  flex 1
  min-height 0
  overflow-y auto
  padding 1rem
  border-radius 0.5rem
  background rgba(0, 0, 0, 0.03)

.faq-messages
  flex 1
  min-height 0
  overflow-y auto
  display flex
  flex-direction column
  gap 0.75rem
  padding-right 0.25rem

.faq-message
  padding 0.75rem
  border-radius 0.75rem
  max-width 95%

  &.user
    align-self flex-end
    background rgba(0, 100, 200, 0.08)

  &.assistant
    align-self flex-start
    background rgba(0, 0, 0, 0.05)

  &.loading
    opacity 0.7

.faq-message-meta
  display flex
  flex-wrap wrap
  gap 0.5rem
  font-size 0.85rem
  opacity 0.7
  margin-bottom 0.35rem

.faq-message-role
  font-weight 600

.faq-message-content
  white-space pre-wrap
  line-height 1.6
  font-size 1.15rem

.faq-chat-form
  display flex
  flex-direction column
  gap 0.75rem
  margin-top 0.5rem
  flex-shrink 0

.faq-chat-input
  width 100%
  padding 0.75rem
  border 1px solid rgba(0, 0, 0, 0.15)
  border-radius 0.5rem
  resize none
  max-height 6rem
  font-family inherit
  font-size 1.05rem
  box-sizing border-box

.faq-chat-empty
  opacity 0.7
  padding 2rem 0
</style>



// WEBPACK FOOTER //
// src/components/pages/faq/Faqs.vue